import React from "react";
import { BodyMeasurementResult } from "../../../utilities/types/unified-services";

interface AnatomyVisualizationProps {
  gender: "Male" | "Female" | "Undefined";
  bodyType: "Plus-size" | "Normal";
  measurements: Array<{
    key: string;
    label: string;
    value: number;
    unit: string;
  }>;
  result?: BodyMeasurementResult;
}

const AnatomyVisualization: React.FC<AnatomyVisualizationProps> = ({ gender, bodyType, measurements, result }) => {
  const getAnatomyImagePath = () => {
    const basePath = "/images/anatomy/";

    if (gender === "Undefined") {
      return `${basePath}neutral-${bodyType.toLowerCase()}.png`;
    }

    return `${basePath}${gender.toLowerCase()}-${bodyType.toLowerCase()}.png`;
  };

  const getAnatomyImageAlt = () => {
    return `${gender} with ${bodyType} body type anatomy visualization`;
  };

  return (
    <div className="anatomy-visualization">
      <div className="flex justify-center">
        <div className="relative max-w-md mx-auto">
          <div className="relative anatomy-image-container">
            <div className={`w-64 mx-auto flex items-center justify-center relative overflow-hidden`}>
              <img
                src={getAnatomyImagePath()}
                alt={getAnatomyImageAlt()}
                className="object-contain h-full w-fit rounded-xl"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  const placeholder = target.nextElementSibling as HTMLElement;
                  if (placeholder) {
                    placeholder.style.display = "flex";
                  }
                }}
              />

              <div className="absolute inset-0 flex items-center justify-center space-y-2 text-center" style={{ display: "none" }}>
                <div>
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 bg-white rounded-full shadow-md">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-600">
                    {gender} - {bodyType}
                  </p>
                  <p className="text-xs text-gray-400">Anatomy image will be loaded here</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-6">
        <div className="p-4 transition-all duration-200 border border-gray-200 bg-gradient-to-br from-gray-50 to-white rounded-xl hover:shadow-md hover:border-primary/30">
          <div className="text-center">
            <div className="mb-2 text-sm font-medium text-gray-700">Gender</div>
            <div className="mb-1 text-2xl font-bold text-secondary">{gender}</div>
          </div>
        </div>
        <div className="p-4 transition-all duration-200 border border-gray-200 bg-gradient-to-br from-gray-50 to-white rounded-xl hover:shadow-md hover:border-primary/30">
          <div className="text-center">
            <div className="mb-2 text-sm font-medium text-gray-700">Body Type</div>
            <div className="mb-1 text-2xl font-bold text-secondary">{bodyType}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnatomyVisualization;
